import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fm } from '../utils';
import './MenuPage.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CategoryPill = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`relative flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
      active
        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25 scale-105'
        : 'bg-white dark:bg-surface-800 text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700 border border-surface-200 dark:border-surface-700'
    }`}
  >
    {children}
  </button>
);

const ProductCard = ({ urun, index }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className="group bg-white dark:bg-surface-800 rounded-2xl overflow-hidden border border-surface-100 dark:border-surface-700 shadow-soft hover:shadow-soft-lg transition-all duration-300 hover:-translate-y-0.5"
      style={{ animation: `fadeInUp 0.4s ease-out ${index * 0.04}s both` }}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-surface-100 dark:bg-surface-700 overflow-hidden">
        {urun.resimBase64 && !imgError ? (
          <>
            <div className={`absolute inset-0 bg-surface-100 dark:bg-surface-700 ${imgLoaded ? 'hidden' : 'block'}`}>
              <div className="w-full h-full animate-pulse-soft" />
            </div>
            <img
              src={urun.resimBase64}
              alt={urun.urunAdi}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-surface-300 dark:text-surface-500">
            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {urun.stokMiktari > 0 && (
          <span className="absolute top-3 right-3 px-2.5 py-1 bg-emerald-500/90 text-white text-[10px] font-semibold rounded-full backdrop-blur-sm shadow-sm">
            Stokta
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-display font-semibold text-surface-800 dark:text-white text-sm leading-tight mb-1">
          {urun.urunAdi}
        </h3>
        {urun.aciklama && (
          <p className="text-[11px] text-surface-400 dark:text-surface-500 leading-relaxed line-clamp-2 mb-3">
            {urun.aciklama}
          </p>
        )}
        <div className="flex items-center justify-between">
          <span className="font-display font-bold text-lg text-emerald-600 dark:text-emerald-400">
            {fm(urun.fiyat)}
          </span>
          {urun.hazirlamaSuresi > 0 && (
            <span className="text-[10px] text-surface-400 dark:text-surface-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {urun.hazirlamaSuresi} dk
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const MenuPage = () => {
  const [kategoriler, setKategoriler] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [seciliKategori, setSeciliKategori] = useState(null);
  const [loading, setLoading] = useState(true);
  const categoryRef = useRef(null);

  useEffect(() => { fetchKategoriler(); }, []);

  useEffect(() => {
    if (seciliKategori) fetchUrunler(seciliKategori);
  }, [seciliKategori]);

  const fetchKategoriler = async () => {
    try {
      const response = await fetch(`${API_URL}/kategoriler`);
      const data = await response.json();
      setKategoriler(data);
      if (data.length > 0) setSeciliKategori(data[0].kategoriID);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    } finally { setLoading(false); }
  };

  const fetchUrunler = async (kategoriId) => {
    try {
      const response = await fetch(`${API_URL}/urunler/kategori/${kategoriId}`);
      setUrunler(await response.json());
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
    }
  };

  const scrollCategory = (direction) => {
    if (!categoryRef.current) return;
    const scrollAmount = 200;
    categoryRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-surface-400 font-medium">Menü yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-surface-800/80 backdrop-blur-xl border-b border-surface-100 dark:border-surface-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <div>
                <h1 className="font-display font-bold text-surface-800 dark:text-white text-lg leading-tight">Neşve Cafe</h1>
                <p className="text-[10px] text-surface-400 dark:text-surface-500 font-medium">Dijital Menü</p>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-surface-500 dark:text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 bg-surface-100 dark:bg-surface-700/50 hover:bg-surface-200 dark:hover:bg-surface-700 rounded-xl transition-all duration-200"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Ana Sayfa
            </Link>
          </div>
        </div>
      </header>

      {/* Categories */}
      <div className="sticky top-16 z-20 bg-white/60 dark:bg-surface-800/60 backdrop-blur-xl border-b border-surface-100 dark:border-surface-700/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {kategoriler.length > 4 && (
              <>
                <button
                  onClick={() => scrollCategory('left')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white dark:bg-surface-700 rounded-full shadow-lg flex items-center justify-center text-surface-500 hover:text-surface-700 dark:hover:text-surface-200 transition-all opacity-0 group-hover:opacity-100"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => scrollCategory('right')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white dark:bg-surface-700 rounded-full shadow-lg flex items-center justify-center text-surface-500 hover:text-surface-700 dark:hover:text-surface-200 transition-all"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
            <div
              ref={categoryRef}
              className="flex gap-2 py-3 overflow-x-auto scrollbar-none custom-scrollbar"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {kategoriler.map(kat => (
                <CategoryPill
                  key={kat.kategoriID}
                  active={seciliKategori === kat.kategoriID}
                  onClick={() => setSeciliKategori(kat.kategoriID)}
                >
                  {kat.kategoriAdi}
                </CategoryPill>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-surface-800 dark:text-white text-lg">
            {urunler.length > 0
              ? `${kategoriler.find(k => k.kategoriID === seciliKategori)?.kategoriAdi || 'Ürünler'} (${urunler.length})`
              : 'Ürünler'}
          </h2>
          {urunler.length > 0 && (
            <span className="text-xs text-surface-400 dark:text-surface-500 font-medium">
              {urunler.length} ürün
            </span>
          )}
        </div>

        {urunler.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-surface-300 dark:text-surface-600">
            <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-sm font-medium">Bu kategoride ürün bulunmuyor</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-5">
            {urunler.map((urun, idx) => (
              <ProductCard key={urun.urunID} urun={urun} index={idx} />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-surface-100 dark:border-surface-700/30 bg-white/50 dark:bg-surface-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[11px] text-surface-400 dark:text-surface-500 font-medium">
              © 2026 Neşve Cafe. Tüm hakları saklıdır.
            </p>
            <p className="text-[10px] text-surface-300 dark:text-surface-600">
              Dijital menü & POS sistemi
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MenuPage;
